
import {
  companiesMapActions,
  companiesMapGetters
} from "@/store/modules/companies";
import Vue from "vue";
import { tableColumnsListCompanies } from "./CompaniesTable";
import { ITableWrapperColumns } from "@/types";
import CompanyTableOperations from "./CompanyTableOperations.vue";
import NoResultsFound from "@/components/NotFound/NoResultsFound.vue";
import { cloneDeep } from "lodash";

interface IOptions {
  label: string;
  faIcon: string;
  class?: string;
  value: string;
}
interface ICompanyCompanyData {
  tableActions: any[];
  errorMessage: string;
  isLoading: boolean;
  isAdmin: boolean;
  loading: boolean;
  successMessage: string;
  companyId: string;
  loadingText: string;
  selectedDocuments: any[];
  queryOverride: { deactivated: boolean };
}
export default Vue.extend({
  name: "listCompanies",
  components: {
    NoResultsFound,
    Exporter: () => import("@/components/Exporter/Exporter.vue")
  },
  data(): ICompanyCompanyData {
    return {
      successMessage: "",
      errorMessage: "",
      isLoading: false,
      isAdmin: true,
      tableActions: ["Export csv"],
      loading: false,
      companyId: "",
      loadingText: "",
      selectedDocuments: [],
      queryOverride: { deactivated: false }
    };
  },
  methods: {
    ...companiesMapActions([
      "getCompanies",
      "deleteCompany",
      "deleteBulkCompanies",
      "restoreBulkCompanies"
    ]),
    onToolbarItemClick(event: string) {
      switch (event) {
        case "createNewCompany":
          this.$router.push(`/admin/companies/create`);
          break;
        case "companies-recycle-bin":
          this.queryOverride = { deactivated: true };
          this.selectedDocuments = [];
          this.fetchPageData({});
          break;
        case "companies":
          this.queryOverride = { deactivated: false };
          this.selectedDocuments = [];
          this.fetchPageData({});
          break;
        case "companies-activities":
          this.$router.push(`/admin/companies/activities`).catch(() => {});
          break;
      }
    },
    async fetchPageData(queryObject: any): Promise<any> {
      try {
        this.loading = true;
        this.loadingText = "Loading companies. Please wait...";
        this.$emit("scrollTop");
        await this.getCompanies({
          query: { ...queryObject, ...this.queryOverride }
        });
      } catch (error) {
        console.error(error);
        this.$bugSnagClient.notify(error);
      } finally {
        this.loading = false;
        this.loadingText = "";
      }
    },

    async viewCallback(dataReceived: any): Promise<any> {
      if (this.viewingRecycleBin) return;
      const { _id } = dataReceived;
      this.$router.push(`/admin/companies/${_id}`).catch(() => {});
    },
    async createCallback(): Promise<any> {
      this.$router.push(`/admin/companies/create`);
    },
    getOperationsTemplate(): any {
      return CompanyTableOperations;
    },
    //
    async handleResponseAction(payload: any) {
      const { data } = payload;
      switch (payload.name) {
        case "duplicate":
          //do nothing
          break;
        case "delete":
          this.deleteCallback(data._id);
          break;
        case "restore":
          this.selectedDocuments = [data];
          this.$modal.show("companiesBulkRestoreModal");
          break;
        case "edit":
          this.$router.push(`/admin/companies/${data._id}`);
          break;
        default:
          this.errorMessage = "Operation specified is not supported";
      }
    },
    // bulk actions
    bulkActionHandler(action: string) {
      switch (action) {
        case "Delete Selected":
          if (!this.selectedDocuments.length) {
            this.errorMessage = "No companies selected.";
            return;
          }
          this.$modal.show("companiesBulkDeleteModal");
          break;
        case "Restore Selected":
          if (!this.selectedDocuments.length) {
            this.errorMessage = "No companies selected.";
            return;
          }
          this.$modal.show("companiesBulkRestoreModal");
          break;
      }
    },
    async doDeleteBulkCompanies(): Promise<void> {
      try {
        this.loadingText = "Deleting selected companies. Please wait...";
        const companyIds = this.selectedDocuments.map((doc: any) => doc._id);
        await this.deleteBulkCompanies(companyIds);
        this.$appNotifySuccess("Selected companies deleted successfully.");
        this.selectedDocuments = [];
        this.fetchPageData({});
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.$modal.hide("companiesBulkDeleteModal");
        this.loadingText = "";
      }
    },
    async doRestoreBulkCompanies(): Promise<void> {
      try {
        this.loadingText = "Restoring selected companies. Please wait...";
        const companyIds = this.selectedDocuments.map((doc: any) => doc._id);
        await this.restoreBulkCompanies(companyIds);
        this.$appNotifySuccess("Selected companies restored successfully.");
        this.selectedDocuments = [];
        this.fetchPageData({});
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.$modal.hide("companiesBulkRestoreModal");
        this.loadingText = "";
      }
    },
    // delete company from here

    async deleteOneCompany(): Promise<void> {
      try {
        this.isLoading = true;
        this.loadingText = "Deleting company. Please wait...";
        await this.deleteCompany(this.companyId);
        this.$modal.hide("companiesDeleteModal");
        this.$appNotifySuccess("Company Successfully Deleted");
      } catch (e) {
        this.errorMessage = e.message;
        this.$bugSnagClient.notify(e);
      } finally {
        this.isLoading = false;
        this.loadingText = "";
      }
    },
    //delete modal
    async deleteCallback(companyId: any): Promise<void> {
      try {
        this.companyId = companyId;
        this.$modal.show("companiesDeleteModal");
      } catch (error) {
        // delete disabled
      }
    },
    // export
    tableActionHandler(action: string) {
      switch (action) {
        case "Export csv":
          this.$modal.show("exportCompaniesModal");
          break;
      }
    }
  },
  computed: {
    ...companiesMapGetters([
      "companies",
      "getTotalRecords",
      "getCompaniesLinks"
    ]),
    tableColumns(): ITableWrapperColumns {
      return cloneDeep(tableColumnsListCompanies);
    },
    viewingRecycleBin(): boolean {
      return this.queryOverride.deactivated === true;
    },
    tableRowActions(): IOptions[] {
      if (this.viewingRecycleBin) {
        const actions: IOptions[] = [];
        if (this.$isCurrentUserAdmin) {
          actions.push({
            label: "Restore",
            value: "restore",
            faIcon: "trash-restore",
            class: "bg-blue-400 text-white hover:text-white hover:bg-blue-500"
          });
        }
        return actions;
      }

      const actions: IOptions[] = [
        { label: "Edit", value: "edit", faIcon: "edit" }
      ];

      if (this.$isCurrentUserAdmin) {
        actions.push({
          label: "Delete",
          value: "delete",
          faIcon: "trash-alt",
          class: "bg-red-400 text-white hover:text-white hover:bg-red-500"
        });
      }

      return actions;
    },
    bulkMenuActions(): { title: string; disabled: boolean }[] {
      if (!this.$isCurrentUserAdmin) return [];
      return [
        {
          title: this.viewingRecycleBin
            ? "Restore Selected"
            : "Delete Selected",
          disabled: false
        }
      ];
    }
  }
});
